import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Users, Settings, X, ChevronDown,
  LogOut, ChevronRight, BarChart2, MailWarning, LineChart,
  UserCog, UserPlus, Users as UsersIcon, Bell, Shield,
  Home, TrendingUp, FileText, Settings as SettingsIcon,
  User,
  CreditCard,
  Wrench,
  ShieldAlert,
  Gauge,
  UserRoundCog,
  SearchCheck,
  ListChecks,
  Clock3,
  BadgeCheck
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchAdminProfile } from './AdminData';
import loader from '../assets/loader.gif';

export const NAV_ITEMS = [
  { label: "My Profile", icon: User, path: "/admin/staff-profile", permissionKey: "My Profile" },
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin/dashboard", permissionKey: "Dashboard", alwaysVisible: true },
  {
    label: "Masters",
    icon: Users,
    path: "/admin/masters",
    permissionKey: "Registered Masters",
    children: [
      { label: "All Masters", path: "/admin/masters/all", icon: User, permissionKey: "All Masters" },
      { label: "Active Masters", path: "/admin/masters/active", icon: Shield, permissionKey: "Active Masters" },
      { label: "InActive Masters", path: "/admin/masters/inactive", icon: Shield, permissionKey: "InActive Masters" },
    ]
  },
  {
    label: "Users",
    icon: Users,
    path: "/admin/users",
    permissionKey: "Registered Users",
    children: [
      { label: "All Users", path: "/admin/users/all", icon: User, permissionKey: "All Users" },
      { label: "Active Users", path: "/admin/users/active", icon: Shield, permissionKey: "Active Users" },
      { label: "InActive Users", path: "/admin/users/inactive", icon: Shield, permissionKey: "InActive Users" },
    ]
  },
  {
    label: "Plans",
    icon: BarChart2,
    path: "/admin/plans",
    permissionKey: "Plans"
  },
  { label: "Analytics", icon: LineChart, path: "/admin/analytics", permissionKey: "Analytics" },
  {
    label: "Complaints",
    icon: MailWarning,
    permissionKey: "Complaints",
    children: [
      { label: "Complaint Types", path: "/admin/complaints/complaint-types", icon: ListChecks, permissionKey: "Complaint Types" },
      { label: "All Complaints", path: "/admin/complaints/all", icon: FileText, permissionKey: "All Complaints" },
      { label: "Pending Complaints", path: "/admin/complaints/pending", icon: Clock3, permissionKey: "Pending Complaints" },
      { label: "In Progress Complaints", path: "/admin/complaints/in-progress", icon: SearchCheck, permissionKey: "In Progress Complaints" },
      { label: "Resolved Complaints", path: "/admin/complaints/resolved", icon: BadgeCheck, permissionKey: "Resolved Complaints" },
    ]
  },
  {
    label: "Staff Management",
    icon: UserCog,
    path: "/admin/staff",
    permissionKey: "Staff Management",
    children: [
      { label: "Roles", path: "/admin/roles", icon: UserCog, permissionKey: "Roles" },
      { label: 'Departments', path: '/admin/departments', icon: UserCog, permissionKey: "Departments" },

      { label: "All Staff", path: "/admin/staff", icon: UsersIcon, permissionKey: "All Staff" },
    ]
  },
  { label: "Notifications", icon: Bell, path: "/admin/notifications", permissionKey: "Notifications" },
  {
    label: "Settings",
    icon: SettingsIcon,
    path: "/admin/profile",
    permissionKey: "Settings",
    children: [
      { label: "Profile", path: "/admin/profile", icon: User, permissionKey: "Admin Profile" },
      { label: "Terms & Policies", path: "/admin/settings", icon: Shield, permissionKey: "Terms & Policies" },
    ]
  },
];

const PERMISSIONS_API_URL = 'https://api.mobilink.tech/api/superAdmin/staff/getrolesandpermissions';


const normalizeKey = (name) => (name || '').trim().toLowerCase();


const flattenPermissions = (rolesAndPermission) => {
  const map = {};

  const visit = (node) => {
    if (!node || typeof node !== 'object') return;

    if (node.ComponentName) {
      map[normalizeKey(node.ComponentName)] = Array.isArray(node.Actions) ? node.Actions : [];
      return;
    }

    if (Array.isArray(node.Sections)) {
      node.Sections.forEach(visit);
      return;
    }



    Object.values(node).forEach((value) => {
      if (value && typeof value === 'object') visit(value);
    });
  };

  (rolesAndPermission || []).forEach(visit);
  return map;
};

const hasPermission = (permissionsMap, permissionKey) => {
  if (!permissionKey) return false;
  const actions = permissionsMap[normalizeKey(permissionKey)];
  return Array.isArray(actions) && actions.length > 0;
};


const fetchStaffPermissions = async (token) => {
  try {
    const res = await fetch(PERMISSIONS_API_URL, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    const data = await res.json();
    if (data?.success) {
      return { success: true, data: data.rolesAndPermission || [] };
    }
    return { success: false, message: data?.message || 'Failed to fetch permissions' };
  } catch (err) {
    return { success: false, message: err.message };
  }
};

export const Sidebar = ({ active, setActive, open, setOpen }) => {
  const [expanded, setExpanded] = useState({});
  const [userData, setUserData] = useState(null);
  const [userType, setUserType] = useState('admin');
  const navigate = useNavigate();
  const location = useLocation();
  const [staffloading, setstaffloading] = useState(true);

  const currentPath = location.pathname;

  const [adminData, setAdminData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [permissionsMap, setPermissionsMap] = useState({});
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  useEffect(() => {
    const loadAdminData = async () => {
      const result = await fetchAdminProfile();

      if (result.success) {
        setAdminData(result.data);

        console.log("Sidebar updated:", result.data);
      } else {
        console.error("Failed to get admin data:", result.message);
      }

      setLoading(false);
      if (userType !== 'admin') {
        setstaffloading(false);
      }
    };


    loadAdminData();


    const handleProfileUpdated = () => {
      console.log("Profile updated → refreshing Sidebar");
      loadAdminData();
    };

    window.addEventListener(
      "adminProfileUpdated",
      handleProfileUpdated
    );

    return () => {
      window.removeEventListener(
        "adminProfileUpdated",
        handleProfileUpdated
      );
    };
  }, []);

  useEffect(() => {

    const adminAuth = sessionStorage.getItem('adminAuth');
    const subAdminAuth = sessionStorage.getItem('subAdminAuth');
    const staffData = sessionStorage.getItem('subAdminData');

    if (adminAuth === 'true') {
      setUserType('admin');
      setUserData({ name: 'Vijay N', role: 'Super Admin', permissions: [] });
      setPermissionsLoading(false);
    } else if (subAdminAuth === 'true' && staffData) {
      const data = JSON.parse(staffData);
      setUserType(data.role);
      setUserData(data);
    } else {
      setPermissionsLoading(false);
    }
  }, []);

  console.log(staffloading)

  useEffect(() => {
    if (!userType) return;

    let cancelled = false;

    const loadPermissions = async () => {
      setPermissionsLoading(true);
      const token = sessionStorage.getItem('subAdminToken');

      if (!token) {
        if (!cancelled) {
          setPermissionsMap({});
          setPermissionsLoading(false);
        }
        return;
      }

      const result = await fetchStaffPermissions(token);

      if (cancelled) return;

      if (result.success) {
        setPermissionsMap(flattenPermissions(result.data));
        console.log("here getrolesandpermissions")
        setstaffloading(false);
      } else {
        console.error("Failed to get role permissions:", result.message);
        setPermissionsMap({});
      }
      setPermissionsLoading(false);
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
  }, [userType]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));


  const closeOnMobile = () => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      setOpen(false);
    }
  };

  const handleLogout = () => {
    if (sessionStorage.getItem("adminAuth") === "true") {
      sessionStorage.removeItem("adminAuth");
      sessionStorage.removeItem("adminData");
      sessionStorage.removeItem("adminToken");
      navigate("/", { replace: true });
    } else if (sessionStorage.getItem("subAdminAuth") === "true") {
      sessionStorage.removeItem("subAdminAuth");
      sessionStorage.removeItem("subAdminData");
      sessionStorage.removeItem("subAdminToken");
      navigate("/staff-login", { replace: true });
    }
  };


  const getFilteredNavItems = () => {
    if (userType === 'admin') {

      return NAV_ITEMS.filter(item => item.path !== '/admin/staff-profile');
    }


    return NAV_ITEMS.map(item => {
      if (item.alwaysVisible) {
        return item;
      }



      if (item.children?.length) {
        const filteredChildren = item.children.filter(child => {
          if (child.showIfAnyChildVisible) return false;
          return hasPermission(permissionsMap, child.permissionKey);
        });


        const resolvedChildren = item.children
          .filter(child => {
            if (child.showIfAnyChildVisible) {
              return filteredChildren.length > 0;
            }
            return hasPermission(permissionsMap, child.permissionKey);
          });

        const parentVisible =
          hasPermission(permissionsMap, item.permissionKey) || resolvedChildren.length > 0;

        if (!parentVisible) return null;

        return {
          ...item,
          children: resolvedChildren
        };
      }


      return hasPermission(permissionsMap, item.permissionKey) ? item : null;
    }).filter(Boolean);
  };

  const filteredNavItems = getFilteredNavItems();


  const getUserInfo = () => {
    if (userType === 'admin') {
      return {
        name: adminData?.name || 'Super Admin',
        role: adminData?.role || 'Super Admin',
        avatar: adminData?.image || 'A',
        grad: 'from-orange-500 to-orange-600'
      };
    }
    return {
      name: userData?.name || 'Staff',
      role: userData?.role || 'Staff',
      avatar: userData?.image || '',
      grad: 'from-orange-500 to-orange-600'
    };
  };

  const userInfo = getUserInfo();

  const handleAvatarClick = () => {
    if (userType === "admin") {
      navigate("/admin/profile");
    } else {
      navigate("/admin/staff-profile");
    }
  };

  if (userType !== 'admin' && staffloading === true) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-[#FFF4E6]">
        <div className="flex flex-col items-center">
          <img
            src={loader}
            alt="Loading..."
            className="w-24 h-24"
          />

          <h1 className="mt-3 text-[#E87511] text-xl font-semibold">
            Loading...
          </h1>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-10 lg:hidden bg-black/30 backdrop-blur-sm"
          onClick={() => setOpen(false)}
        />
      )}

      <aside
        className={`
          fixed top-0 left-0 h-full z-30 flex flex-col
          transition-all duration-300 overflow-hidden
          ${open ? "translate-x-0 w-64" : "-translate-x-full w-64"}
          lg:static lg:h-screen lg:shrink-0
          ${open
            ? "lg:translate-x-0 lg:w-64 border-r border-orange-200/30"
            : "lg:translate-x-0 lg:w-0 lg:border-r-0"
          }
          bg-white shadow-lg
        `}
      >
        {/* Decorative element */}
        <div className="absolute pointer-events-none w-48 h-48 -top-10 -left-10 rounded-full bg-orange-500/5" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 shrink-0 h-16 border-b border-orange-200/30">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center rounded-xl text-white font-bold">
              <img src='/logo.png' className='w-8 h-8 object-cover' alt="logo" />
            </div>
            <span className="font-bold text-lg tracking-tight text-orange-500">{userInfo.role === 'superAdmin' ? 'RVV Tech Admin' : 'RVV Tech Staff'}</span>
          </div>
          <button
            className="lg:hidden transition-colors text-orange-500 hover:text-orange-600"
            onClick={() => setOpen(false)}
          >
            <X size={18} />
          </button>
        </div>

        {/* User profile */}
        {/* <div className="px-4 py-3 border-b border-orange-200/20">
          <div className="flex items-center gap-3 rounded-xl p-3 bg-orange-50/80 border border-orange-200/30">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold shrink-0 overflow-hidden ${userInfo.role === 'Staff' ? 'text-orange-500 bg-white border border-orange-200' : `text-white bg-gradient-to-br ${userInfo.grad}`}`}>
              <div onClick={handleAvatarClick} className="w-9 h-9 rounded-xl overflow-hidden bg-gradient-to-br from-orange-500 to-orange-600">
                {userInfo.avatar ? (
                  <img
                    src={userInfo.avatar}
                    alt="Admin"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="flex items-center justify-center w-full h-full text-white font-semibold">
                    A
                  </span>
                )}
              </div>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate text-gray-900">{userInfo.name}</p>
              <p className="text-xs text-orange-500">{userInfo.role}</p>
            </div>
          </div>
        </div> */}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-0.5">
          {userType === 'staff' && permissionsLoading ? (

            <div className="space-y-2 px-1 py-2 animate-pulse">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-9 rounded-xl bg-orange-50" />
              ))}
            </div>
          ) : (
            filteredNavItems.map(item => {
              const isActive =
                currentPath === item.path ||
                currentPath.startsWith(item.path + "/") ||
                (item.children &&
                  item.children.some(child =>
                    currentPath === child.path ||
                    currentPath.startsWith(child.path + "/")
                  ));
              const hasChildren = item.children?.length > 0;
              const isExpanded =
                expanded[item.path] ||
                item.children?.some(child =>
                  currentPath === child.path ||
                  currentPath.startsWith(child.path + "/")
                );

              return (
                <div key={item.permissionKey}>
                  <button
                    onClick={() => {
                      if (hasChildren) {
                        toggle(item.path);
                      } else {
                        navigate(item.path);
                        closeOnMobile();
                      }
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium 
                      transition-all relative
                      ${isActive
                        ? 'bg-orange-50 text-orange-500 border border-orange-300/50'
                        : 'text-gray-600 border border-transparent hover:bg-orange-50/50 hover:text-orange-500'
                      }
                    `}
                  >
                    {isActive && (
                      <span className="absolute left-0 top-1/2 -translate-y-1/2 rounded-r w-0.5 h-5 bg-gradient-to-b from-orange-500 to-orange-600 shadow-lg shadow-orange-500/50" />
                    )}
                    <item.icon size={16} className={isActive ? 'text-orange-500' : 'text-gray-400'} />
                    <span className="flex-1 text-left">{item.label}</span>
                    {hasChildren && (
                      <ChevronDown
                        size={13}
                        className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""} ${isActive ? 'text-orange-500' : 'text-gray-400'}`}
                      />
                    )}
                  </button>

                  {hasChildren && isExpanded && (
                    <div className="ml-5 mt-0.5 space-y-0.5 pl-3 border-l border-orange-200/30">
                      {item.children.map((child) => (
                        <button
                          key={child.path}
                          onClick={() => {
                            navigate(child.path);
                            closeOnMobile();
                          }}
                          className={`
                            w-full flex items-center gap-2 text-left text-xs px-2 py-2 rounded-lg 
                            transition-colors
                            ${currentPath === child.path ||
                              currentPath.startsWith(child.path + "/")
                              ? 'text-orange-500 font-semibold bg-orange-50/50'
                              : 'text-gray-500 hover:text-orange-500 hover:bg-orange-50/30'
                            }
                          `}
                        >
                          {child.icon && <child.icon size={12} />}
                          <ChevronRight size={10} className="opacity-50" />
                          {child.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </nav>

        {/* Footer */}
        <div className="px-3 pb-4 shrink-0 border-t border-orange-200/20">
          <button
            onClick={handleLogout}
            className="w-full mt-3 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors"
          >
            <LogOut size={16} />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
};